import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, ShieldCheck, Save, FileDown, Eye } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { SignatureCanvas } from "@/components/SignatureCanvas";
import { exportSafetyInstructionToPDF } from "@/lib/contractPdfExport";
import { saveDocumentToEmployeeFile, getDocumentCategory, getDocumentTypeName } from "@/lib/documentStorage";
import { PdfPreviewDialog } from "./PdfPreviewDialog";

interface Employee {
  id: string;
  name: string;
  position: string;
  department: string | null;
}

interface CompanySettings {
  name: string | null;
  street: string | null;
  house_number: string | null;
  postal_code: string | null;
  city: string | null;
}

export const SafetyInstructionForm = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [companySettings, setCompanySettings] = useState<CompanySettings | null>(null);
  const [showSignature, setShowSignature] = useState(false);
  const [previewDoc, setPreviewDoc] = useState<any>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  
  
  const [formData, setFormData] = useState({
    selectedEmployeeId: "",
    personName: "",
    position: "",
    department: "",
    documentDate: new Date().toISOString().split("T")[0],
    supervisorName: "",
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [employeesRes, companyRes] = await Promise.all([
        supabase.from("employees").select("id, name, position, department").is("archived_at", null),
        supabase.from("company_settings").select("name, street, house_number, postal_code, city").single()
      ]);

      if (employeesRes.data) setEmployees(employeesRes.data);
      if (companyRes.data) setCompanySettings(companyRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEmployeeSelect = (employeeId: string) => {
    const employee = employees.find(e => e.id === employeeId);
    if (!employee) return;

    setFormData(prev => ({
      ...prev,
      selectedEmployeeId: employeeId,
      personName: employee.name,
      position: employee.position,
      department: employee.department || "",
    }));
  };

  const handleSave = async (signatureUrl?: string) => {
    if (!user) return;
    if (!formData.selectedEmployeeId) {
      toast.error("Bitte wählen Sie einen Mitarbeiter aus");
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("contract_templates").insert({
        template_type: "safety_instruction",
        employee_id: formData.selectedEmployeeId,
        person_name: formData.personName,
        position: formData.position,
        department: formData.department,
        issue_date: formData.documentDate,
        custom_text: formData.supervisorName,
        signature_url: signatureUrl,
        signed_at: signatureUrl ? new Date().toISOString() : null,
        signed_by: signatureUrl ? formData.selectedEmployeeId : null,
        status: signatureUrl ? "signed" : "draft",
        created_by: user.id,
      });

      if (error) throw error;

      // Create + archive PDF to employee documents
      try {
        const pdfDoc = await exportSafetyInstructionToPDF(
          {
            personName: formData.personName,
            position: formData.position,
            department: formData.department,
            documentDate: formData.documentDate,
            supervisorName: formData.supervisorName,
            companyName: companySettings?.name || "Flashtec-Solution GmbH",
            signatureUrl,
          },
          false
        );

        const category = getDocumentCategory("safety_instruction");
        const typeName = getDocumentTypeName("safety_instruction");
        const result = await saveDocumentToEmployeeFile(
          formData.selectedEmployeeId,
          `${typeName} - ${formData.documentDate}`,
          category,
          pdfDoc,
          user.id
        );

        if (result.success) {
          toast.success("Sicherheitsunterweisung gespeichert und in Dokumente abgelegt");
        } else {
          console.warn("Dokument konnte nicht gespeichert werden:", result.error);
          toast.error("Sicherheitsunterweisung gespeichert, PDF-Ablage fehlgeschlagen");
        }
      } catch (e) {
        console.error("Error archiving PDF:", e);
        toast.error("Sicherheitsunterweisung gespeichert, PDF-Ablage fehlgeschlagen");
      }

      setShowSignature(false);
      resetForm();
    } catch (error) {
      console.error("Error saving safety instruction:", error);
      toast.error("Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const buildPdfPayload = () => ({
    personName: formData.personName,
    position: formData.position,
    department: formData.department,
    documentDate: formData.documentDate,
    supervisorName: formData.supervisorName,
    companyName: companySettings?.name || "Flashtec-Solution GmbH",
  });

  const handlePreview = async () => {
    if (!formData.selectedEmployeeId) {
      toast.error("Bitte wählen Sie einen Mitarbeiter aus");
      return;
    }
    try {
      const pdfDoc = await exportSafetyInstructionToPDF(buildPdfPayload(), false);
      setPreviewDoc(pdfDoc);
      setPreviewOpen(true);
    } catch (e) {
      console.error("Error generating preview:", e);
      toast.error("Fehler beim Erstellen der Vorschau");
    }
  };

  const handleExportPDF = async () => {
    if (!formData.selectedEmployeeId) {
      toast.error("Bitte wählen Sie einen Mitarbeiter aus");
      return;
    }

    try {
      const pdfDoc = await exportSafetyInstructionToPDF({
        personName: formData.personName,
        position: formData.position,
        department: formData.department,
        documentDate: formData.documentDate,
        supervisorName: formData.supervisorName,
        companyName: companySettings?.name || "Flashtec-Solution GmbH",
      }, false);

      // Save to employee documents
      if (user) {
        const category = getDocumentCategory('safety_instruction');
        const typeName = getDocumentTypeName('safety_instruction');
        const result = await saveDocumentToEmployeeFile(
          formData.selectedEmployeeId,
          `${typeName} - ${formData.documentDate}`,
          category,
          pdfDoc,
          user.id
        );
        if (result.success) {
          toast.success("PDF wurde erstellt und in den Mitarbeiter-Dokumenten gespeichert");
        } else {
          console.warn("Dokument konnte nicht gespeichert werden:", result.error);
        }
      }

      // Always download the PDF for the user
      pdfDoc.save(`Sicherheitsunterweisung_${formData.personName.replace(/\s+/g, "_")}.pdf`);
      toast.success("PDF wurde erstellt");
    } catch (error) {
      console.error("Error exporting PDF:", error);
      toast.error("Fehler beim Erstellen des PDF");
    }
  };

  const resetForm = () => {
    setFormData({
      selectedEmployeeId: "",
      personName: "",
      position: "",
      department: "",
      documentDate: new Date().toISOString().split("T")[0],
      supervisorName: "",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          Sicherheitsunterweisung erstellen
        </CardTitle>
        <CardDescription>
          Erstellen Sie eine Sicherheitsunterweisung nach Arbeitsschutzgesetz (ArbSchG) und DGUV-Vorschriften
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Employee Selection */}
        <div className="space-y-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium">Mitarbeiter auswählen</h4>
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <Label>Mitarbeiter *</Label>
              <Select value={formData.selectedEmployeeId} onValueChange={handleEmployeeSelect}>
                <SelectTrigger>
                  <SelectValue placeholder="Mitarbeiter wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {employees.map(emp => (
                    <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Dokumentendatum</Label>
              <Input 
                type="date"
                value={formData.documentDate} 
                onChange={(e) => setFormData(prev => ({ ...prev, documentDate: e.target.value }))}
              />
            </div>
            <div>
              <Label>Unterweisender (Führungskraft)</Label>
              <Input 
                value={formData.supervisorName} 
                onChange={(e) => setFormData(prev => ({ ...prev, supervisorName: e.target.value }))}
                placeholder="Name der unterweisenden Person"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Name</Label>
              <Input value={formData.personName} readOnly className="bg-muted" />
            </div>
            <div>
              <Label>Position / Abteilung</Label>
              <Input value={`${formData.position}${formData.department ? ` - ${formData.department}` : ""}`} readOnly className="bg-muted" />
            </div>
          </div>
        </div>

        {/* Info about content */}
        <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
          <h4 className="font-medium text-primary mb-2">Inhalt der Sicherheitsunterweisung</h4>
          <p className="text-sm text-muted-foreground mb-2">
            Das Dokument enthält alle relevanten Sicherheitsthemen gemäß ArbSchG und DGUV-Vorschriften:
          </p>
          <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
            <li>Rechtliche Grundlagen und Ziel der Unterweisung</li>
            <li>Verantwortung und Mitwirkungspflicht</li>
            <li>Verhalten auf Baustellen</li>
            <li>Persönliche Schutzausrüstung (PSA)</li>
            <li>Stolper-, Rutsch- und Sturzgefahren</li>
            <li>Arbeiten mit Leitern und in der Höhe</li>
            <li>Elektrische Gefährdungen</li>
            <li>Besondere Gefahren bei Glasfaserarbeiten</li>
            <li>Werkzeuge, Maschinen und Arbeitsmittel</li>
            <li>Ordnung, Sauberkeit und Brandschutz</li>
            <li>Verhalten bei Unfällen und Notfällen</li>
            <li>Arbeitsmedizinische Vorsorge</li>
            <li>Konsequenzen bei Verstößen</li>
          </ul>
        </div>

        {/* Signature Section */}
        {showSignature && (
          <div className="space-y-4 p-4 border rounded-lg">
            <h4 className="font-medium">Unterschrift des Mitarbeiters</h4>
            <p className="text-sm text-muted-foreground">
              Mit der Unterschrift bestätigt der Mitarbeiter, dass er die Sicherheitsunterweisung erhalten und den Inhalt verstanden hat.
            </p>
            <SignatureCanvas
              onSave={async (signatureDataUrl) => {
                const blob = await fetch(signatureDataUrl).then(r => r.blob());
                const fileName = `safety-instruction-signature-${Date.now()}.png`;
                const { data: uploadData, error: uploadError } = await supabase.storage
                  .from("employee-profiles")
                  .upload(`signatures/${fileName}`, blob);

                if (uploadError) throw uploadError;

                const { data: urlData } = supabase.storage
                  .from("employee-profiles")
                  .getPublicUrl(`signatures/${fileName}`);

                await handleSave(urlData.publicUrl);
                setShowSignature(false);
              }}
              title="Unterschrift Mitarbeiter"
              description="Bitte unterschreiben Sie zur Bestätigung der Sicherheitsunterweisung"
              confirmationText="Ich bestätige, dass ich diese Sicherheitsunterweisung erhalten habe, den Inhalt verstanden habe und die Sicherheitsregeln einhalte."
            />
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={handlePreview} disabled={saving} className="gap-2">
            <Eye className="h-4 w-4" />
            Vorschau
          </Button>
          <Button onClick={() => handleSave()} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Als Entwurf speichern
          </Button>
          <Button variant="outline" onClick={() => setShowSignature(true)} className="gap-2">
            <ShieldCheck className="h-4 w-4" />
            Mit Unterschrift speichern
          </Button>
          <Button variant="outline" onClick={handleExportPDF} className="gap-2">
            <FileDown className="h-4 w-4" />
            PDF exportieren
          </Button>
        </div>
      </CardContent>
      <PdfPreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        pdfDoc={previewDoc}
        title="Vorschau: Sicherheitsunterweisung"
        fileName={`Sicherheitsunterweisung_${formData.personName.replace(/\s+/g, "_") || "Vorschau"}.pdf`}
      />
    </Card>
  );
};
